import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";

import { useAuthState } from "@/HelloCashflowDomain/Authentication/hooks/useAuthState";
import {
    clearStoredPreviewEmailOverride,
    getStoredPreviewEmailOverride,
    isPreviewEnvironment,
    setStoredPreviewEmailOverride,
} from "@/utils/previewEmailOverride";

const PREVIEW_WARNING_TEXT =
    "Preview mode sends are blocked unless this is set. Use a test address that can receive all preview emails.";

const normalizeInputEmail = (value: string): string => value.trim().toLowerCase();

const isValidEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const PreviewEmailOverrideGate = () => {
    const { user, loading } = useAuthState();
    const isPreview = isPreviewEnvironment();

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    useEffect(() => {
        if (!isPreview || loading || !user) {
            setOpen(false);
            return;
        }

        const nextOverride = getStoredPreviewEmailOverride() ?? "";
        setValue(nextOverride);
        setError("");

        if (nextOverride) {
            setOpen(false);
            return;
        }

        setOpen(true);
    }, [isPreview, loading, user]);

    const handleSubmit = () => {
        const normalized = normalizeInputEmail(value);
        if (!isValidEmail(normalized)) {
            setError("Enter a valid email address for preview delivery.");
            return;
        }

        setStoredPreviewEmailOverride(normalized);
        setOpen(false);
        setError("");
    };

    const handleClear = () => {
        clearStoredPreviewEmailOverride();
        setValue("");
        setError("");
    };

    if (!isPreview || !user || !open) {
        return null;
    }

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle>Set Preview Email Override</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    {PREVIEW_WARNING_TEXT}
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Test recipient email"
                    value={value}
                    onChange={event => {
                        setValue(event.target.value);
                        setError("");
                    }}
                    error={Boolean(error)}
                    helperText={error}
                    type="email"
                />
                <Box mt={2}>
                    <Alert severity="warning">
                        The override is required before sending any email from preview.
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={handleClear}>
                    Clear stored email
                </Button>
                <Button variant="contained" onClick={handleSubmit}>
                    Save override
                </Button>
            </DialogActions>
        </Dialog>
    );
};
